import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';
import { Typography, Input, Button, enqueueSnackbar } from '../../../design-system';
import {
  AuthHeaderBase,
  AuthFormBase,
  AuthFooterBase,
  AuthFooterLink,
  AuthFooterTypography,
  AuthFooterLanguage,
} from '../AuthComponents';
import { useCallbackOnKeyDown, useIsSmallScreen, useSigninMutation } from '../../../hooks';
import { REGEX, INPUT_ERRORS, MESSAGES } from '../../../const';
import { i18n } from '../../../i18n';
import { getDefaultRouter } from '../../../utils';

export const SignInHeader = () => {
  return (
    <AuthHeaderBase>
      <Typography variant={'h4'} align={'center'} weight={'bold'}>
        {i18n.t('pages.signin.title')}
      </Typography>
    </AuthHeaderBase>
  );
};

export const SignInForm = () => {
  const navigate = useNavigate();
  const { mutate } = useSigninMutation();
  const isSmallScreen = useIsSmallScreen();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const validators = {
    email: !email
      ? INPUT_ERRORS.REQUIRED.EMAIL
      : !REGEX.EMAIL.test(email)
      ? INPUT_ERRORS.NOT_VALID.EMAIL
      : !validator.isLength(email, { min: 6, max: 50 })
      ? INPUT_ERRORS.NOT_VALID.EMAIL_LENGTH
      : '',
    password: !password ? INPUT_ERRORS.REQUIRED.PASSWORD : '',
  };

  const handleSubmitClick = () => {
    if (!Object.values(validators).every((isError) => !isError)) {
      setIsSubmitClicked(true);
      return;
    }

    mutate(
      { email, password },
      {
        onSuccess: () => {
          const path = getDefaultRouter();
          navigate(path);
        },
        onError: () => enqueueSnackbar(MESSAGES.AUTH.ERROR.NOT_LOGGED_IN, 'error'),
        onSettled: () => setIsSubmitClicked(false),
      },
    );
  };
  useCallbackOnKeyDown(handleSubmitClick, 'Enter');

  return (
    <AuthFormBase>
      <Input
        defaultValue={email}
        onChange={(e) => setEmail(e.target.value)}
        label={i18n.t('label.email')}
        placeholder={i18n.t('placeholder.email')}
        name={'email'}
        type={'email'}
        autoComplete={'email'}
        data-size={'large'}
        withoutSpace
        error={isSubmitClicked && validators.email}
        helperText={isSubmitClicked ? validators.email : ''}
      />
      <Input
        defaultValue={password}
        onChange={(e) => setPassword(e.target.value)}
        label={i18n.t('label.password')}
        placeholder={i18n.t('placeholder.password')}
        name={'password'}
        type={'password'}
        autoComplete={'current-password'}
        data-size={'large'}
        withoutSpace
        error={isSubmitClicked && validators.password}
        helperText={isSubmitClicked && validators.password ? INPUT_ERRORS.REQUIRED.PASSWORD : ''}
      />
      <Button data-color={'primary'} size={'large'} onClick={handleSubmitClick}>
        {i18n.t('button.signIn')}
      </Button>
      {isSmallScreen && <AuthFooterLanguage />}
    </AuthFormBase>
  );
};

interface SignInFooterProps {
  handleSendSignUpClick: () => void;
}
export const SignInFooter = ({ handleSendSignUpClick }: SignInFooterProps) => {
  return (
    <AuthFooterBase>
      <AuthFooterTypography
        variant={'textS'}
        color={'--primary-100'}
        onClick={handleSendSignUpClick}
      >
        {i18n.t('pages.signin.dontHaveAnAccount')}
      </AuthFooterTypography>
      <Typography variant={'textS'} color={'--primary-100'}>
        <AuthFooterLink to={'/forgotpassword'} withunderline>
          {i18n.t('pages.signin.forgotYourPassword')}
        </AuthFooterLink>
      </Typography>
    </AuthFooterBase>
  );
};
